/**
 * Соответствие адресов полной и мобильной версии (specs/001-pwa-shell/contracts/routes.md).
 *
 * Здесь единственное место, где функции разделов регистрируют свои мобильные аналоги:
 * `registerPath("/finance-requests", "/mobile/finance")`. Адреса без аналога открываются
 * экраном «Раздел пока доступен в полной версии» (`/mobile/full?to=…`).
 *
 * Все адреса — «браузерные», то есть с префиксом `/mobile`. Роутер мобильной версии работает
 * с `basename: "/mobile"`, поэтому для `navigate` адрес нужно сначала пропустить через
 * `stripBase`.
 */

/** Адрес мобильной версии в браузере */
export const MOBILE_BASE = "/mobile";

/** Экран-заглушка «Раздел пока доступен в полной версии» */
export const FULL_VERSION_PATH = `${MOBILE_BASE}/full`;

interface PathPair {
    full: string;
    mobile: string;
}

const initialPairs = (): PathPair[] => [
    { full: "/", mobile: MOBILE_BASE },
    { full: "/login", mobile: `${MOBILE_BASE}/login` },
    // табель (specs/002-pwa-home-timesheet, contracts/routes.md)
    { full: "/spending/timesheet", mobile: `${MOBILE_BASE}/timesheet` },
    // финансовые запросы (specs/003-pwa-finance-requests, contracts/routes.md)
    { full: "/finance-requests", mobile: `${MOBILE_BASE}/finance` },
    // штатка (specs/005-pwa-staffing, contracts/routes.md)
    { full: "/spending/schedule", mobile: `${MOBILE_BASE}/staff` },
    // задания (specs/004-pwa-tasks, contracts/routes.md): параметры ссылок уведомлений отбрасываются
    { full: "/order-questions", mobile: `${MOBILE_BASE}/tasks` },
];

/** Адреса с параметром `:имя` — сегмент пути, который переносится как есть */
const initialPatterns = (): PathPair[] => [
    // постоянная ссылка на задание (specs/004-pwa-tasks R7)
    { full: "/order-questions/:id", mobile: `${MOBILE_BASE}/tasks/:id` },
    // адреса объектов в уведомлениях (specs/006-pwa-notifications, contracts/routes.md): карточка
    // финансового запроса, документ на подпись, событие календаря, запрос
    { full: "/finance-requests/:id", mobile: `${MOBILE_BASE}/finance/:id` },
    { full: "/signatures/:id", mobile: `${MOBILE_BASE}/signatures/:id` },
    { full: "/calendar/:id", mobile: `${MOBILE_BASE}/calendar/:id` },
    { full: "/requests/:id", mobile: `${MOBILE_BASE}/requests/:id` },
];

let pairs: PathPair[] = initialPairs();
let patterns: PathPair[] = initialPatterns();

/** Адрес без хвостового слэша (корень остаётся `/`) */
function normalize(pathname: string): string {
    return pathname.length > 1 ? pathname.replace(/\/+$/, "") || "/" : pathname;
}

/** Разбор «путь?параметры#якорь» без привязки к текущему адресу страницы */
function split(address: string): { pathname: string; search: string; hash: string } {
    const hashIndex = address.indexOf("#");
    const withoutHash = hashIndex < 0 ? address : address.slice(0, hashIndex);
    const hash = hashIndex < 0 ? "" : address.slice(hashIndex);
    const index = withoutHash.indexOf("?");

    return index < 0
        ? { pathname: withoutHash, search: "", hash }
        : { pathname: withoutHash.slice(0, index), search: withoutHash.slice(index), hash };
}

/**
 * Push о подписи (specs/009-pwa-signatures R5): сервер ведёт на вкладку «Проект» договора с файлом в
 * якоре — `/orders/{id}#tab=project&rowId=…&fileId=…`. В мобильной версии это документ на подпись
 * по файлу. Без `fileId` или с другой вкладкой — `null`, адрес идёт обычным путём.
 */
function signatureFilePath(pathname: string, hash: string): string | null {
    if (!/^\/orders\/[^/]+$/.test(normalize(pathname)) || !hash) {
        return null;
    }

    const params = new URLSearchParams(hash.slice(1));
    const fileId = params.get("fileId") ?? "";

    return params.get("tab") === "project" && /^[0-9a-f]{24}$/i.test(fileId)
        ? `${MOBILE_BASE}/signatures/file/${fileId}`
        : null;
}

/** Убрать служебный параметр запуска со значка (`source=pwa`), остальные оставить */
function withoutSource(search: string): string {
    const params = new URLSearchParams(search);

    params.delete("source");

    const rest = params.toString();

    return rest ? `?${rest}` : "";
}

/** Адрес полной версии допустим, только если это путь внутри сайта */
function isSafeFullPath(path: string): boolean {
    return path.startsWith("/") && !path.startsWith("//") && !path.startsWith("/\\");
}

/**
 * Зарегистрировать мобильный аналог страницы полной версии.
 * Повторная регистрация того же адреса полной версии заменяет прежнюю.
 */
export function registerPath(full: string, mobile: string): void {
    const key = normalize(full);

    pairs = pairs.filter((pair) => pair.full !== key);
    pairs.push({ full: key, mobile: normalize(mobile) });
}

/**
 * Зарегистрировать аналог адреса с параметрами: `registerPattern("/order-questions/:id",
 * "/mobile/tasks/:id")`. Шаблоны проверяются после точных адресов; параметр — один сегмент пути.
 */
export function registerPattern(full: string, mobile: string): void {
    const key = normalize(full);

    patterns = patterns.filter((pattern) => pattern.full !== key);
    patterns.push({ full: key, mobile: normalize(mobile) });
}

/** Вернуть таблицу и шаблоны к начальному состоянию (для тестов) */
export function resetPaths(): void {
    pairs = initialPairs();
    patterns = initialPatterns();
}

/** Значения параметров `path` по шаблону `pattern` или `null`, если не подходит */
function matchPattern(pattern: string, path: string): Record<string, string> | null {
    const expected = pattern.split("/");
    const actual = path.split("/");

    if (expected.length !== actual.length) {
        return null;
    }

    const values: Record<string, string> = {};

    for (let i = 0; i < expected.length; i++) {
        if (expected[i].startsWith(":")) {
            if (!actual[i]) {
                return null;
            }

            values[expected[i].slice(1)] = actual[i];
        } else if (expected[i] !== actual[i]) {
            return null;
        }
    }

    return values;
}

/** Подставить значения параметров в шаблон */
function fillPattern(pattern: string, values: Record<string, string>): string {
    return pattern
        .split("/")
        .map((part) => (part.startsWith(":") ? values[part.slice(1)] ?? part : part))
        .join("/");
}

/** Убрать префикс `/mobile`: `/mobile/login` → `/login`, `/mobile` → `/` */
export function stripBase(path: string): string {
    const { pathname, search } = split(path);

    if (pathname === MOBILE_BASE) {
        return "/" + search;
    }

    return pathname.startsWith(`${MOBILE_BASE}/`) ? pathname.slice(MOBILE_BASE.length) + search : path;
}

/**
 * Адрес в мобильной версии для адреса полной версии: есть аналог — он, иначе экран
 * «Раздел пока доступен в полной версии» с этим адресом в параметре `to` — вместе с якорем: в нём
 * полная версия держит вкладку и строку (`/orders/{id}#tab=content&rowId=…`). Аналогу якорь не
 * передаётся. Параметр запуска со значка (`source=pwa`) в результат не попадает.
 */
export function toMobile(fullPath: string): string {
    const { pathname, search, hash } = split(fullPath);
    const clean = withoutSource(search);
    const signature = signatureFilePath(pathname, hash);

    if (signature) {
        return signature;
    }

    const pair = pairs.find((item) => item.full === normalize(pathname));

    if (pair) {
        return pair.mobile;
    }

    for (const pattern of patterns) {
        const values = matchPattern(pattern.full, normalize(pathname));

        if (values) {
            return fillPattern(pattern.mobile, values);
        }
    }

    return `${FULL_VERSION_PATH}?to=${encodeURIComponent(pathname + clean + hash)}`;
}

/**
 * Адрес, на который мобильная версия заменяет текущий при запуске, или `null`, если менять не нужно:
 * - без сети запуск со значка (`/?source=pwa`) отдаёт `mobile.html` прямо на адресе полной
 *   версии — роутер с `basename: "/mobile"` его бы не узнал, переводим в мобильный аналог;
 * - параметр запуска со значка (`source=pwa`) из адреса мобильной версии убираем (FR-024).
 * Якорь push о подписи становится адресом документа (specs/009 R5) и дальше не переносится.
 */
export function launchAddress(pathname: string, search: string, hash: string = ""): string | null {
    const inMobile = pathname === MOBILE_BASE || pathname.startsWith(`${MOBILE_BASE}/`);

    if (!inMobile) {
        const signature = signatureFilePath(pathname, hash);

        if (signature) {
            return signature;
        }

        // у заглушки якорь уже в `to`; аналогу он остаётся якорем (`#comments` карточки задания)
        const target = toMobile(pathname + search + hash);

        return target.startsWith(`${FULL_VERSION_PATH}?`) ? target : target + hash;
    }

    const clean = withoutSource(search);

    return clean === search ? null : pathname + clean + hash;
}

/**
 * Адрес полной версии для адреса мобильной: с экрана-заглушки — её `to`; для остальных —
 * обратное соответствие из таблицы, иначе `/`. Небезопасный `to` (чужой сайт) отбрасывается.
 */
export function toFull(mobilePath: string): string {
    const { pathname, search } = split(mobilePath);
    const base = normalize(pathname);

    if (base === FULL_VERSION_PATH || base === "/full") {
        const to = new URLSearchParams(search).get("to") ?? "";

        return isSafeFullPath(to) ? to : "/";
    }

    const pair = pairs.find((item) => item.mobile === base || stripBase(item.mobile) === base);

    if (pair) {
        return pair.full;
    }

    for (const pattern of patterns) {
        const values = matchPattern(pattern.mobile, base) ?? matchPattern(stripBase(pattern.mobile), base);

        if (values) {
            return fillPattern(pattern.full, values);
        }
    }

    return "/";
}

/**
 * Адрес из уведомления → адрес в роутере мобильной версии (specs/006 R2): адрес мобильной версии
 * (`/mobile/…`) — как есть без префикса, адрес полной — через соответствие (`toMobile`, вместе с
 * якорем: в нём push о подписи передаёт файл, specs/009 R5).
 */
export function inAppPath(url: string): string {
    const { pathname } = split(url);

    if (pathname === MOBILE_BASE || pathname.startsWith(`${MOBILE_BASE}/`)) {
        return stripBase(url);
    }

    return stripBase(toMobile(url));
}
